// client/src/pages/EditProfile.js
import React, { useEffect, useState } from 'react';
import { apiFetch, apiJson, refreshSessionUser } from '../lib/api';
import { AddressAutofill } from '@mapbox/search-js-react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/chitconnect-logo-blue.png';
import '../App.css';

export default function EditProfile() {
  const navigate = useNavigate();
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [form, setForm] = useState({
    given_name: '',
    surname: '',
    email: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
    time_zone: currentTimeZone,
  });

  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [updateTZ, setUpdateTZ] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Prefer /api/users/me if present; normalize { ok, user } or plain user
        let res = await apiFetch('/api/users/me');
        if (!res.ok) res = await apiFetch('/api/me');
        if (!res.ok) throw new Error('load-failed');

        const json = await res.json();
        const u = json && json.user ? json.user : json;

        if (!mounted) return;
        setForm(prev => ({
          ...prev,
          given_name: u.given_name || u.givenName || '',
          surname: u.surname || u.last_name || '',
          email: u.email || '',
          mobile: u.mobile || '',
          address: u.address || '',
          city: u.city || '',
          state: u.state || '',
          postcode: u.postcode || '',
          country: u.country || '',
          time_zone: u.time_zone || u.timezone || currentTimeZone,
        }));
      } catch (err) {
        if (!mounted) return;
        console.error('Failed to fetch user:', err);
        setMessage('⚠️ Unable to load current profile; fields start empty.');
      }
    })();
    return () => { mounted = false; };
  }, [currentTimeZone]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleAddressSelect = async (event) => {
    const feature = event.features?.[0];
    if (!feature) return;

    const props = feature.properties || {};
    const context = feature.context || [];

    const fullStreet = props.address_line1 || feature.place_name || '';
    const city = props.place || '';
    const state = props.region || '';
    const postcode = props.postcode || '';

    const countryContext = context.find(c => c.id.startsWith('country'));
    let country = form.country;
    if (countryContext?.short_code) {
      country = countryContext.short_code.toUpperCase();
    } else if (props.country) {
      country = String(props.country).toUpperCase();
    } else if (feature.place_name) {
      const parts = feature.place_name.split(',');
      country = parts[parts.length - 1].trim().toUpperCase();
    }

    // Try to infer time zone from coordinates (optional)
    let timeZone = form.time_zone;
    const geometry = feature.geometry;
    if (geometry?.coordinates) {
      const [lng, lat] = geometry.coordinates;
      try {
        const res = await fetch(
          `https://api.timezonedb.com/v2.1/get-time-zone?key=${process.env.REACT_APP_TIMEZONE_API_KEY}&format=json&by=position&lat=${lat}&lng=${lng}`
        );
        const data = await res.json();
        if (data?.zoneName) timeZone = data.zoneName;
      } catch (e) {
        console.error('Failed to fetch timezone:', e);
      }
    }

    setForm(prev => ({
      ...prev,
      address: fullStreet,
      city,
      state,
      postcode,
      country,
      time_zone: timeZone
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.given_name?.trim()) next.given_name = 'Given name is required';
    if (!form.surname?.trim()) next.surname = 'Surname is required';
    if (!form.email?.trim()) next.email = 'Email is required';
    if (form.postcode && !/^\w[\w\s-]{2,10}$/.test(form.postcode)) next.postcode = 'Postcode looks invalid';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        time_zone: updateTZ ? currentTimeZone : form.time_zone,
      };

      const res = await apiJson('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // Refresh cached user from the server (cookie session)
        await refreshSessionUser();
        setMessage('✅ Profile updated successfully.');
        setTimeout(() => navigate('/'), 800);
      } else {
        setMessage(`❌ ${res.data?.error || 'Failed to update profile.'}`);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('❌ Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="register-page">
      <img src={logo} alt="ChitConnect Logo" className="register-logo" />
      <form onSubmit={handleSubmit} className="register-form" autoComplete="on">
        <h2>Edit Profile</h2>
        {message && <p className="register-message">{message}</p>}

        <label>
          Given Name
          <input type="text" name="given_name" value={form.given_name || ''} onChange={handleChange} />
          {errors.given_name && <span className="field-error">{errors.given_name}</span>}
        </label>

        <label>
          Surname
          <input type="text" name="surname" value={form.surname || ''} onChange={handleChange} />
          {errors.surname && <span className="field-error">{errors.surname}</span>}
        </label>

        <label>
          Email
          <input
            type="email"
            name="email"
            value={form.email || ''}
            onChange={handleChange}
            autoComplete="email"
          />
          {errors.email && <span className="field-error">{errors.email}</span>}
        </label>

        <label>
          Mobile
          <input
            type="text"
            name="mobile"
            value={form.mobile || ''}
            onChange={handleChange}
            autoComplete="tel"
          />
        </label>

        <label>
          Address
          <AddressAutofill
            accessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            onRetrieve={handleAddressSelect}
          >
            <input
              type="text"
              name="address"
              value={form.address || ''}
              onChange={handleChange}
              autoComplete="address-line1"
            />
          </AddressAutofill>
        </label>

        <label>
          City
          <input
            type="text"
            name="city"
            value={form.city || ''}
            onChange={handleChange}
            autoComplete="address-level2"
          />
        </label>

        <label>
          State
          <input
            type="text"
            name="state"
            value={form.state || ''}
            onChange={handleChange}
            autoComplete="address-level1"
          />
        </label>

        <label>
          Postcode
          <input
            type="text"
            name="postcode"
            value={form.postcode || ''}
            onChange={handleChange}
            autoComplete="postal-code"
          />
          {errors.postcode && <span className="field-error">{errors.postcode}</span>}
        </label>

        <label>
          Country
          <input
            type="text"
            name="country"
            value={form.country || ''}
            onChange={handleChange}
            autoComplete="country-name"
          />
        </label>

        {form.time_zone !== currentTimeZone && (
          <>
            <div style={{ marginTop: '20px', fontStyle: 'italic', color: '#555' }}>
              Stored Time Zone: {form.time_zone}<br />
              Current Time Zone: {currentTimeZone}
            </div>
            <label style={{ marginTop: '10px' }}>
              <input
                type="checkbox"
                checked={updateTZ}
                onChange={(e) => setUpdateTZ(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Update with the current time zone
            </label>
          </>
        )}

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}
